import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Clock, 
  Zap, 
  ArrowRight,
  AlertTriangle
} from 'lucide-react';

export function TrialExpiredModal({ isOpen, daysRemaining, onClose }) {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  const isExpired = daysRemaining <= 0;
  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 5;

  const handleUpgrade = () => {
    navigate('/billing');
    if (onClose) onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={isExpired ? undefined : onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="border-2 border-warning/50 shadow-2xl overflow-hidden">
            <CardContent className="p-0">
              {/* Header */}
              <div className={`px-6 py-8 text-center ${isExpired ? 'bg-destructive/10' : 'bg-warning/10'}`}>
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${isExpired ? 'bg-destructive/20' : 'bg-warning/20'}`}>
                  {isExpired ? (
                    <AlertTriangle className="w-8 h-8 text-destructive" />
                  ) : (
                    <Clock className="w-8 h-8 text-warning" />
                  )}
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  {isExpired ? 'Trial Expired' : 'Trial Ending Soon'}
                </h2>
                <p className="text-muted-foreground">
                  {isExpired 
                    ? 'Your 30-day trial has ended. Upgrade to continue monitoring your Telegram channels.'
                    : `You have ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left in your trial.`
                  }
                </p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {isExpired && (
                  <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-2">What happens now?</p>
                    <ul className="space-y-1">
                      <li>• Your monitors are paused</li>
                      <li>• You won&apos;t receive any alerts</li>
                      <li>• Your data is safe and will be restored after upgrade</li>
                    </ul>
                  </div>
                )}

                {/* Upgrade options */}
                <div className="space-y-3">
                  <Button
                    onClick={handleUpgrade}
                    className="w-full h-12 gap-2 rounded-xl text-base"
                    data-testid="upgrade-btn"
                  >
                    <Zap className="w-5 h-5" />
                    Upgrade Now
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                  
                  {!isExpired && (
                    <Button
                      variant="ghost"
                      onClick={onClose}
                      className="w-full rounded-xl"
                    >
                      Remind me later
                    </Button>
                  )}
                </div>

                {/* Pricing hint */}
                <p className="text-center text-sm text-muted-foreground">
                  Plans start at <span className="font-semibold text-foreground">$19/month</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function TrialBanner({ daysRemaining, onUpgrade }) {
  if (daysRemaining === null || daysRemaining === undefined) return null;
  if (daysRemaining > 7) return null; // Only show when 7 days or less
  
  const isExpired = daysRemaining <= 0;
  const isUrgent = daysRemaining <= 3;

  const getBannerClass = () => {
    if (isExpired) return 'bg-destructive text-destructive-foreground';
    if (isUrgent) return 'bg-warning text-warning-foreground';
    return 'bg-primary/10 text-primary';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`px-4 py-2 text-center text-sm font-medium ${getBannerClass()}`}
    >
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {isExpired ? (
          <>
            <AlertTriangle className="w-4 h-4" />
            <span>Your trial has expired.</span>
          </>
        ) : (
          <>
            <Clock className="w-4 h-4" />
            <span>
              {daysRemaining === 1 
                ? 'Last day of your trial!' 
                : `${daysRemaining} days left in your trial.`}
            </span>
          </>
        )}
        <Button 
          size="sm" 
          variant={isExpired ? "secondary" : "default"}
          className="h-7 px-3 text-xs rounded-full"
          onClick={onUpgrade}
        >
          Upgrade now
        </Button>
      </div>
    </motion.div>
  );
}

export default TrialExpiredModal;
